import React, {useMemo} from 'react'
import {useTranslation} from 'react-i18next'

import {Theme} from '@emotion/react'
import {Autocomplete, SxProps, TextField} from '@mui/material'

import {useAllCountries} from '@progyny/hooks/useAllCountries'

type FieldError = {hasError?: boolean; message?: string} | string | undefined

type RenderCountrySelectorProps = {
  id: string
  field: string
  label: string
  isReview?: boolean
  values?: Record<string, string>
  errors?: Record<string, FieldError>
  handleOnChange: (e: {target: {name: string; value: string}}) => void
  sharedTextFieldStyles?: SxProps<Theme>
  reviewValue?: string
}

const RenderCountrySelector: React.FC<RenderCountrySelectorProps> = (props) => {
  const {t} = useTranslation()
  const {data} = useAllCountries()

  const selectedCountry = useMemo(() => {
    const currentCode =
      props.isReview && props.reviewValue
        ? props.reviewValue
        : props.values?.[props.field]
    return data?.allCountries?.find((c) => c?.code === currentCode) || null
  }, [
    props.values,
    props.field,
    props.isReview,
    props.reviewValue,
    data?.allCountries,
  ])

  const fieldError = props.errors?.[props.field]

  const errorMessage =
    typeof fieldError === 'string' ? fieldError : fieldError?.message

  return (
    <Autocomplete
      id={props.id}
      options={data?.allCountries!}
      getOptionLabel={(option) => option?.name!}
      value={selectedCountry}
      onChange={(_, newValue) => {
        props.handleOnChange({
          target: {
            name: props.field,
            value: newValue?.code || '',
          },
        })
      }}
      disabled={props.isReview}
      sx={{
        '& .MuiFormControl-root': {my: 0, pt: 0},
        '& .MuiInputBase-root': {py: '14px !important'},
        '& .MuiInputLabel-root': {py: 1},
      }}
      renderInput={(params) => (
        <TextField
          {...params}
          name={props.field}
          label={t(props.label)}
          required
          error={!!errorMessage}
          helperText={errorMessage}
          placeholder={t(
            'budget_partner_agreement_helpers_render_country_selector_choose_countr'
          )}
          size='small'
          variant='outlined'
          margin='dense'
          sx={props.sharedTextFieldStyles}
        />
      )}
    />
  )
}

export default RenderCountrySelector
